import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ProximityToggle } from "@/components/ProximityToggle";
import { SocialConsent, SOCIAL_MSG, MessagesConsent } from "@/components/SocialConsent";
import { RulesBox, RULES_MSG } from "@/components/RulesBox";
import { ArrowLeft, Camera, X, Plus, Crosshair, Image as ImageIcon, Briefcase } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { COUNTRIES, PHONE_CODES, CITIES_BY_COUNTRY, cityCoords, nearCountries } from "@/lib/places";
import { SUBCATEGORIES } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LocationPicker } from "@/components/LocationPicker";
import { useFormCfg, useFormSub, SecTitle, ExtraFields } from "@/lib/formcfg";
import { useCredits, Paid, PhotosNote, CreditTotal, creditShort } from "@/lib/credits";
import { CityInput, AddressInput } from "@/components/AddressFields";

const SUBS = SUBCATEGORIES.pune;

const JOB_TYPES = ["Kohë e plotë", "Gjysmë kohe", "Sezonale", "Praktikë", "Me kontratë", "Orar fleksibël"];
const SALARY_UNITS = ["/muaj", "/orë", "/ditë", "/javë", "/vit"];
const BENEFITS = ["Sigurim shëndetësor", "Transport", "Ushqim", "Banim", "Bonus", "Trajnim", "Orar fleksibël", "Mundësi avancimi"];

function imgUrl(path) { return path; }
async function uploadImage(file) {
  const fd = new FormData(); fd.append("file", file);
  const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return imgUrl(r.data.url);
}
function parsePhone(p) { if (!p) return { code: "+46", num: "" }; const m = p.trim().match(/^(\+\d+)\s*(.*)$/); return m ? { code: m[1], num: m[2].replace(/\s/g, "") } : { code: "+46", num: p }; }

function Card({ icon: Icon, title: t0, children, desc }) {
  const fc = useFormCfg();
  if (fc.hidden(t0) && !fc.edit) return null;
  const title = <SecTitle t={t0} />;
  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mt-3">
      <div className="flex items-center gap-2 mb-1">{Icon && <Icon className="w-4 h-4 text-emerald-500" />}<h2 className="font-bold text-slate-800 text-sm uppercase tracking-wide">{title}</h2></div>
      {desc && <p className="text-xs text-slate-400 mb-3">{desc}</p>}
      <div className={desc ? "" : "mt-3"}>{children}</div>
    </section>
  );
}
const label = "text-sm font-semibold text-slate-700 mb-1 block";
const fld = "w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none";

export function PuneForm({ onDone, initial }) {
  const nav = useNavigate();
  const { requestGPS } = useLocation();
  const d0 = initial?.data || {};
  const [saving, setSaving] = useState(false);
  const [pa, setPa] = useState(!!initial?.proximity_alert);
  const [sa, setSa] = useState(initial?.social_allowed ?? null);
  const [am, setAm] = useState(initial?.allow_messages ?? true);
  const [ruleOk, setRuleOk] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(initial?.images || []);
  const cr = useCredits("pune", initial);
  const [logo, setLogo] = useState(d0.logo || "");
  const [logoScale, setLogoScale] = useState(d0.logo_scale || 1);
  const [f, setF] = useState({
    name: initial?.title || "", subcategory: initial?.subcategory || "", description: initial?.description || "",
    company: d0.company || "", job_type: d0.job_type || "", experience: d0.experience || "", requirements: d0.requirements || "",
    salary: initial?.price != null ? String(initial.price) : "", salary_unit: initial?.price_unit || "/muaj",
    country: initial?.country || "", city: initial?.city || "", address: initial?.address || "", postal_code: initial?.postal_code || "",
    instagram: initial?.instagram || "", facebook: initial?.facebook || initial?.data?.facebook || "", website: initial?.website || "", email: initial?.email || "", notes: d0.notes || "",
  });
  useFormSub(f.subcategory);
  const [coords, setCoords] = useState(initial?.lat != null ? { lat: initial.lat, lng: initial.lng } : null);
  const [phone, setPhone] = useState(parsePhone(initial?.phone));
  const [hasWhats, setHasWhats] = useState(initial ? !!initial.whatsapp : true);
  const [benefits, setBenefits] = useState(d0.benefits || []);

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - images.length);
    if (!files.length) return;
    setUploading(true);
    try { for (const file of files) { const url = await uploadImage(file); setImages((p) => [...p, url]); } toast.success("Fotot u ngarkuan"); }
    catch (er) { toast.error(errMsg(er)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const pickLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await uploadImage(file); setLogo(url); toast.success("Logoja u ngarkua"); }
    catch (er) { toast.error(errMsg(er)); } finally { if (logoRef.current) logoRef.current.value = ""; }
  };
  const removeImg = (i) => setImages((p) => p.filter((_, x) => x !== i));
  const makeMain = (i) => setImages((p) => { const a = [...p]; const [m] = a.splice(i, 1); return [m, ...a]; });
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const gps = async () => { try { const c = await requestGPS(); setCoords({ lat: c.lat, lng: c.lng }); toast.success("Vendndodhja u ruajt"); } catch { toast.error("Nuk u mor vendndodhja"); } };

  const submit = async () => {
    if (creditShort(cr, { logo: !!logo, address: !!f.address })) return toast.error("Nuk keni GjejKredit të mjaftueshme");
    if (sa === null && cr.shown("social")) { toast.error(SOCIAL_MSG); return; }
    if (!ruleOk) { toast.error(RULES_MSG); return; }
    if (!f.name) return toast.error("Shkruaj titullin e punës");
    if (!f.subcategory) return toast.error("Zgjidh pozitën");
    if (!f.country || !f.city) return toast.error("Plotëso vendndodhjen");
    setSaving(true);
    const c = coords || cityCoords(f.country, f.city);
    const payload = {
      category: "pune", subcategory: f.subcategory, title: f.name, description: f.description,
      price: f.salary ? parseFloat(f.salary) : null, price_unit: f.salary ? f.salary_unit : "",
      country: f.country, city: f.city, address: f.address, postal_code: f.postal_code || "",
      lat: c?.lat ?? null, lng: c?.lng ?? null, images,
      phone: phone.num ? `${phone.code} ${phone.num}` : "",
      whatsapp: phone.num && hasWhats ? `${phone.code}${phone.num}` : "",
      instagram: f.instagram, facebook: f.facebook, website: f.website, email: f.email, proximity_alert: pa, social_allowed: sa, allow_messages: am,
      data: { loc_auto: !coords, logo, logo_scale: logoScale, company: f.company, job_type: f.job_type, experience: f.experience, requirements: f.requirements, benefits, notes: f.notes },
    };
    cr.strip(payload);
    try {
      const r = initial ? await api.put(`/listings/${initial.id}`, payload) : await api.post("/listings", payload);
      toast.success(initial ? "U dërgua për aprovim" : "U publikua me sukses!");
      onDone ? onDone(initial ? initial.id : r.data.id) : nav(`/shpallje/${initial ? initial.id : r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="gs-fade px-4 pt-4 pb-10 bg-slate-50">
      <button data-testid="pune-back" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />{initial ? "Anulo" : "Publiko"}</button>
      <h1 className="text-2xl font-extrabold text-slate-900">{initial ? "Ndrysho" : "Publiko"}: Punë</h1>
      <p className="text-sm text-slate-400 mt-0.5">Shto pozitën e punës me kompani, orar, pagë dhe kërkesa.</p>

      <Card icon={Camera} title="Foto (opsionale)" desc="Foto të kompanisë ose vendit të punës.">
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickFiles} className="hidden" data-testid="pune-file-input" />
        <div className="grid grid-cols-3 gap-2">
          {images.map((src, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200">
              <img src={src} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">Kryesore</span>}
              <button onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
              {i !== 0 && <button onClick={() => makeMain(i)} className="absolute bottom-1 left-1 bg-white/90 text-slate-700 text-[9px] font-semibold px-1.5 py-0.5 rounded">Bëj kryesore</button>}
            </div>
          ))}
          {images.length < cr.maxImg && <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pune-add-photo" className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 flex flex-col items-center justify-center text-emerald-600 gap-1"><Plus className="w-6 h-6" /><span className="text-xs font-semibold">{uploading ? "Duke ngarkuar..." : "Shto foto"}</span></button>}
        </div>
        <PhotosNote cr={cr} count={images.length} />
      </Card>

      <Card title="Informacioni">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-20 h-20 rounded-full bg-slate-900 ring-2 ring-slate-100 overflow-hidden flex items-center justify-center shrink-0">
            {logo ? <img src={logo} alt="logo" style={{ transform: `scale(${logoScale})` }} className="w-full h-full object-cover" /> : <ImageIcon className="w-7 h-7 text-slate-400" />}
          </div>
          <div className="flex-1">
            <label className={label}>Logoja e kompanisë</label>
            <input ref={logoRef} type="file" accept="image/*" onChange={pickLogo} className="hidden" data-testid="pune-logo-input" />
            <button type="button" onClick={() => logoRef.current?.click()} data-testid="pune-logo-btn" className="text-sm font-semibold text-emerald-600 bg-emerald-50 rounded-xl px-3 py-2">{logo ? "Ndrysho logon" : "Shto logo"}</button>
            {logo && <div className="mt-2"><div className="text-xs text-slate-400 mb-1">Madhësia e logos</div><input type="range" min="0.5" max="2.5" step="0.05" value={logoScale} onChange={(e) => setLogoScale(parseFloat(e.target.value))} className="w-full accent-emerald-500" /></div>}
          </div>
        </div>
        <label className={label}>Titulli i punës *</label>
        <Input data-testid="pune-name" placeholder="p.sh. Kamerier/e në restorant" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11" />
        <label className={`${label} mt-3`}>Pozita *</label>
        <select data-testid="pune-subcategory" value={f.subcategory} onChange={(e) => setF({ ...f, subcategory: e.target.value })} className={fld}>
          <option value="">Zgjidh...</option>{SUBS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className={`${label} mt-3`}>Përshkrimi i punës</label>
        <Textarea data-testid="pune-description" placeholder="Përshkruaj detyrat dhe punën..." value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" />
      </Card>

      <Card icon={Briefcase} title="Detajet e punës">
        <label className={label}>Kompania</label>
        <Input data-testid="pune-company" placeholder="p.sh. Restaurant Dardania" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="rounded-xl h-11" />
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div><label className={label}>Orari</label><select data-testid="pune-job-type" value={f.job_type} onChange={(e) => setF({ ...f, job_type: e.target.value })} className={fld}><option value="">Zgjidh</option>{JOB_TYPES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><label className={label}>Përvoja</label><Input data-testid="pune-experience" placeholder="p.sh. 1-2 vjet" value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} className="rounded-xl h-11" /></div>
        </div>
        <label className={`${label} mt-3`}>Paga</label>
        <div className="flex gap-2">
          <Input data-testid="pune-salary" inputMode="numeric" placeholder="p.sh. 1500" value={f.salary} onChange={(e) => setF({ ...f, salary: e.target.value })} className="rounded-xl h-11 flex-1" />
          <select value={f.salary_unit} onChange={(e) => setF({ ...f, salary_unit: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl h-11 px-2 text-sm outline-none">{SALARY_UNITS.map((s) => <option key={s}>{s}</option>)}</select>
        </div>
      </Card>

      <Card title="Kërkesat">
        <Textarea data-testid="pune-requirements" placeholder="Çfarë kërkohet nga kandidati..." value={f.requirements} onChange={(e) => setF({ ...f, requirements: e.target.value })} className="rounded-xl" />
      </Card>

      <Card title="Përfitimet">
        <div className="grid grid-cols-2 gap-2">
          {useFormCfg().opts("benefits", BENEFITS).map((s) => (
            <button key={s} data-testid={`benefit-${s}`} onClick={() => toggle(benefits, setBenefits, s)} className={`flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl border text-left ${benefits.includes(s) ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200 text-slate-600"}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${benefits.includes(s) ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>{benefits.includes(s) && <span className="text-white text-[10px]">✓</span>}</span>{s}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Vendndodhja">
        <div className="grid grid-cols-2 gap-2">
          <div><label className={label}>Shteti *</label><select data-testid="pune-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, city: "" })} className={`${fld} px-2`}><option value="">Zgjidh</option>{nearCountries().map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}</select></div>
          <div><label className={label}>Qyteti *</label><CityInput setCoords={setCoords} testid="pune-city" country={f.country} value={f.city} onChange={(v) => setF((x) => ({ ...x, city: v }))} cities={Object.keys(CITIES_BY_COUNTRY[f.country] || {})} className={`${fld} px-2 disabled:text-slate-400`} /></div>
        </div>
        <label className={`${label} mt-3`}>Adresa (opsionale)</label>
        <AddressInput testid="pune-address" placeholder="Rruga, numri, zona" f={f} setF={setF} setCoords={setCoords} className="w-full rounded-xl h-11 border border-slate-200 px-3 text-sm" />
        <Paid cr={cr} k="map"><LocationPicker coords={coords} setCoords={setCoords} country={f.country} city={f.city} address={f.address} testid="pune-loc" /></Paid>
      </Card>

      <Paid cr={cr} k="contact"><Card title="Kontakt për aplikim" desc="Si të aplikojnë kandidatët.">
        <div className="space-y-3">
          <div>
            <label className={label}>Telefon</label>
            <div className="flex gap-2">
              <select data-testid="pune-phone-code" value={phone.code} onChange={(e) => setPhone({ ...phone, code: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl h-11 px-2 text-sm font-semibold outline-none">{PHONE_CODES.map((c) => <option key={c.dial} value={c.dial}>{c.flag} {c.dial}</option>)}</select>
              <Input data-testid="pune-phone" inputMode="tel" placeholder="Numri i telefonit" value={phone.num} onChange={(e) => setPhone({ ...phone, num: e.target.value })} className="rounded-xl h-11 flex-1" />
            </div>
          </div>
          <button type="button" data-testid="toggle-whatsapp" onClick={() => setHasWhats(!hasWhats)} className={`w-full flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl border ${hasWhats ? "border-green-400 bg-green-50 text-green-700" : "border-slate-200 text-slate-500"}`}><span className={`w-4 h-4 rounded border flex items-center justify-center ${hasWhats ? "bg-green-500 border-green-500" : "border-slate-300"}`}>{hasWhats && <span className="text-white text-[10px]">✓</span>}</span>Ky numër ka WhatsApp</button>
          <div><label className={label}>Email</label><Input data-testid="pune-email" placeholder="email@kompania.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-xl h-11" /></div>
          <div><label className={label}>Instagram</label><Input data-testid="pune-instagram" placeholder="username ose link" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} className="rounded-xl h-11" /></div>
          <div><label className={label}>Facebook</label><Input data-testid="pune-facebook" placeholder="faqja ose link" value={f.facebook} onChange={(e) => setF({ ...f, facebook: e.target.value })} className="rounded-xl h-11" /></div>
          <div><label className={label}>Web faqja e biznesit</label><Input data-testid="pune-website" placeholder="www.biznesi.com" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="rounded-xl h-11" /></div>
        </div>
      </Card></Paid>

      <Card title="Komente">
        <Textarea data-testid="pune-notes" placeholder="Shkruaj ndonjë informacion shtesë..." value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-xl" />
      </Card>

      <Paid cr={cr} k="proximity"><div className="mt-4"><ProximityToggle value={pa} onChange={setPa} /></div></Paid>
      <Paid cr={cr} k="social"><SocialConsent value={sa} onChange={setSa} /></Paid>
      <Paid cr={cr} k="messages"><MessagesConsent value={am} onChange={setAm} /></Paid>
      <RulesBox catKey="pune" accepted={ruleOk} onAccept={setRuleOk} />
      <CreditTotal cr={cr} extra={{ logo: !!logo, address: !!f.address }} />
      <ExtraFields />
      <Button data-testid="pune-submit" onClick={submit} disabled={saving || creditShort(cr, { logo: !!logo, address: !!f.address })} className="w-full py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 font-bold text-base mt-4 shadow-lg shadow-emerald-500/30">{saving ? "Duke ruajtur..." : initial ? "Ruaj ndryshimet" : "Publiko"}</Button>
    </div>
  );
}

export default PuneForm;
